import InnerContainer from "@/components/ContentContainer/InnerContainer";
import { EcoInventSuggestions } from "@/components/ImpactReferenceSelector/EcoInventSuggestions";
import ImpactReferenceSelector, {
  ImpactReferenceSelectedContentProps,
} from "@/components/ImpactReferenceSelector/ImpactReferenceSelector";
import useAnalytics from "@/hooks/useAnalytics";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useTranslation from "@/hooks/useTranslation";
import { message } from "antd";
import { useState } from "react";
import { useBulkPatchMaterial, useGetMaterial } from "../useMaterials";
import styles from "./MaterialDetail.module.scss";

export default function MaterialImpact({ materialId }: { materialId: number }) {
  const { data, isLoading } = useGetMaterial({ materialId });
  const [activeTab, setActiveTab] = useState<string>("suggestions");
  const [selectedDataset, setSelectedDataset] = useState<ImpactDataset>();
  const bulkPatch = useBulkPatchMaterial();

  const analytics = useAnalytics();
  const translation = useTranslation();

  const onSubmit = () => {
    if (selectedDataset) {
      return bulkPatch
        .mutateAsync({
          materialIds: [materialId],
          patch: {
            impact_dataset_id: selectedDataset.id,
          },
        })
        .then(() => {
          analytics.track(`Impact Reference Updated`);
          message.success("Impact reference updated");
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  const onDelete = () =>
    bulkPatch
      .mutateAsync({
        materialIds: [materialId],
        patch: {
          clear: ["impact_dataset_id"],
        },
      })
      .then(() => {
        message.success(`Impact reference deleted.`);
        analytics.track("Impact Reference Deleted");
      })
      .catch(() => {
        message.error(translation.errors.general);
      });

  const selectedContent: ImpactReferenceSelectedContentProps = {
    type: "material",
    name: [data?.name, data?.supplier_name, data?.supplier_address].filter((x) => x).join(" • "),
  };

  const onActiveTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const onDatasetChange = (dataset?: ImpactDataset) => {
    setSelectedDataset(dataset);
  };

  const loadingStates = {
    data: isLoading,
    onDatasetConfirm: bulkPatch.isLoading,
  };

  const ecoInventSuggestionTabContent = (
    <EcoInventSuggestions
      materialIds={[materialId]}
      selectedDataset={selectedDataset}
      onDatasetChange={setSelectedDataset}
      setActiveTab={setActiveTab}
    />
  );

  return (
    <InnerContainer
      title="Impact reference"
      contentClassName={styles.MaterialDetails}
    >
      <ImpactReferenceSelector
        confirmedDataset={data?.match}
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        activeTab={activeTab}
        onActiveTabChange={onActiveTabChange}
        loading={loadingStates}
        modalTitle={"Edit impact reference"}
        showIncomplete={true}
        onDatasetConfirm={onSubmit}
        onDelete={onDelete}
        suggestionTabContent={ecoInventSuggestionTabContent}
        defaultDatabaseFilter={{ special_activity_type: ["market activity", "market group"] }}
        selectedContent={selectedContent}
      />
    </InnerContainer>
  );
}
